import { RAIZ_REVISAO, type RevisaoNodo, type TipoNodoAjuste } from "@/api/metas.service";
import { reaisInteiros } from "./metasFormato";

// Revisão das metas calculadas: a árvore loja → categorias → produto mostra a
// meta calculada e a meta ajustada de cada ramo. A árvore é carregada um nível
// por vez (`revisao_nodos`, ver metas.service.ts): ao abrir o cenário só as
// lojas chegam; expandir um nó traz os filhos dele. Um cenário tem ~120 mil
// nós — trazer tudo de uma vez levava minutos.
//
// Cada nó tem o valor que o motor calculou para ele (o motor grava uma linha
// por nível, então categoria e loja não são soma de produtos). O ajuste é
// gravado no nó em que foi feito, com o mesmo efeito que APLICAR_AJUSTES
// aplica no recálculo (MANUAL.md §6): o total do nó fica fixo e a subárvore
// inteira escala na mesma proporção; os ancestrais acompanham a diferença.
// `avaliador` reproduz isso para o que está carregado. Fixo no indicador
// VENDA — a tela ainda não expõe os demais (CONTRIBUICAO/MARGEM/PERDA).
//
// A tela nunca mostra centavos: todo valor que entra (calculado, ajuste salvo,
// histórico de vendas, digitação) é arredondado para cima ao real inteiro na
// borda (`reaisInteiros`), e daí em diante a conta toda é em inteiros.

/** Nó carregado da revisão — loja (tipoNodo EMPRESA), categoria ou produto (folha). */
export interface NoMeta {
  id: string;
  nome: string;
  codigo: string;
  tipoNodo: TipoNodoAjuste;
  /** Loja dona do nó (a mesma categoria/produto existe uma vez por loja); `null` quando o ERP não informa. */
  codEmpresa: number | null;
  /** Profundidade a partir da loja (0) — calculada localmente, não vem de tb_flash_nodo.nivel. */
  nivel: number;
  paiId: string | null;
  /** O que o motor sugeriu (origem CALCULADO), antes de qualquer ajuste — a base de comparação. */
  calculado: number;
  /** Contribuição do ajuste calculada pelo motor para este nó — vem pronta do ERP, não é derivada no cliente. */
  contribuicaoCalculada: number;
  /** % de contribuição resolvido pelo motor pra este nó — vem pronto do ERP; `null` quando caiu no fallback de margem histórica. */
  percContribuicaoEnviado: number | null;
  /** Meta de perda que o motor calculou para este nó, em reais inteiros — base do relatório comercial. */
  perdaCalculada: number;
  /** Venda realizada em cada mês da janela, do mais antigo ao mais recente. */
  historico: number[];
}

/** Meta informada por nó, em reais inteiros, indexada pelo id — só os nós com ajuste próprio. */
export type Valores = Record<string, number>;

/**
 * O que já foi carregado. `filhos` só tem entrada para os nós cujos filhos
 * vieram completos (por expansão ou "Exibir até"); um nó que chegou via
 * busca/ajuste salvo está em `nos`, mas o pai dele pode não ter `filhos`.
 */
export interface Arvore {
  nos: Record<string, NoMeta>;
  filhos: Record<string, string[]>;
  lojas: string[];
}

export const ARVORE_VAZIA: Arvore = { nos: {}, filhos: {}, lojas: [] };

/** Até onde a árvore abre por padrão: só a loja. */
export const PROFUNDIDADE_PADRAO = 0;

/** Teto do "Exibir até" enquanto o ERP não informa a profundidade real (`revisao_profundidade`) — ou se não souber. */
export const PROFUNDIDADE_MAXIMA = 4;

export function rotuloNivel(no: NoMeta) {
  if (no.tipoNodo === "EMPRESA") return "Loja";
  if (no.tipoNodo === "PRODUTO") return `SKU ${no.codigo}`.trim();
  return `N${no.nivel}`;
}

export function ordenarPorNome(ids: string[], nos: Record<string, NoMeta>) {
  return [...ids].sort((a, b) => (nos[a]?.nome ?? "").localeCompare(nos[b]?.nome ?? ""));
}

/**
 * Acrescenta linhas do ERP à árvore sem mexer em `filhos`: quem chama sabe se
 * a carga trouxe os filhos completos de alguém (`aplicarCarga`) ou só um
 * caminho (busca, ajustes salvos). O nível é resolvido pelo pai — que ou já
 * estava carregado ou veio no mesmo lote, já que toda carga é de cima para
 * baixo.
 */
export function incorporar(arvore: Arvore, linhas: RevisaoNodo[]): Arvore {
  const nos = { ...arvore.nos };
  linhas.forEach((linha) => {
    nos[linha.idNodo] = {
      id: linha.idNodo,
      nome: linha.nome,
      codigo: linha.codigo,
      tipoNodo: linha.tipoNodo,
      codEmpresa: linha.codEmpresa !== null && Number.isFinite(Number(linha.codEmpresa)) ? Number(linha.codEmpresa) : null,
      paiId: linha.idNodoPai,
      nivel: -1,
      calculado: linha.valorMensalInteiro ?? reaisInteiros(linha.valorMensalCalculado),
      contribuicaoCalculada: reaisInteiros(linha.valorContribuicaoCalculado),
      percContribuicaoEnviado: linha.percContribuicaoEnviado,
      perdaCalculada: reaisInteiros(linha.valorPerdaCalculado),
      historico: linha.historico.map(reaisInteiros),
    };
  });
  const nivelDe = (id: string): number => {
    const no = nos[id];
    if (!no) return -1;
    if (no.nivel >= 0) return no.nivel;
    no.nivel = no.paiId && nos[no.paiId] ? nivelDe(no.paiId) + 1 : 0;
    return no.nivel;
  };
  linhas.forEach((linha) => nivelDe(linha.idNodo));
  return { ...arvore, nos };
}

/** Soma o `historico` de cada `id` mês a mês (mesma janela para todos) — usada tanto pelo total de lojas selecionadas quanto pela visão Grupo. */
export function somarHistoricos(ids: string[], nos: Record<string, NoMeta>): number[] {
  return ids.reduce<number[]>((acc, id) => {
    (nos[id]?.historico ?? []).forEach((venda, i) => {
      acc[i] = (acc[i] ?? 0) + venda;
    });
    return acc;
  }, []);
}

/**
 * Arredondar cada nó ao real por conta própria faz a soma dos filhos divergir
 * do pai em alguns reais. Aqui, para cada pai cujos filhos chegaram completos,
 * o inteiro do pai é repartido entre eles pelos maiores restos
 * (`distribuirProporcional`): a soma fecha exatamente, e cada filho fica a
 * menos de R$ 1 do valor do motor. Só vale quando o pai do motor é mesmo a
 * soma dos filhos (dentro do ruído de arredondamento) — quando não é
 * (propagar_pai, exclusões), cada nó mantém o próprio valor. Altera só os nós
 * do lote, que `incorporar` acabou de criar; de cima para baixo, para que o
 * inteiro reconciliado do pai seja o alvo dos filhos.
 *
 * O valor calculado só passa por aqui quando o ERP não mandou o inteiro
 * (`valorMensalInteiro`, manifesto antigo): o ERP reparte sobre a árvore
 * inteira, e repartir de novo na tela faria o nó voltar a depender do que já
 * foi carregado. O histórico continua sendo reconciliado aqui.
 */
function reconciliarFilhos(
  nos: Record<string, NoMeta>,
  raiz: string,
  porPai: Record<string, string[]>,
  linhas: RevisaoNodo[],
  niveis: number,
  relativo: (linha: RevisaoNodo) => number,
) {
  const bruto: Record<string, number> = {};
  const brutoHistorico: Record<string, number[]> = {};
  const inteiroDoErp = new Set<string>();
  linhas.forEach((linha) => {
    bruto[linha.idNodo] = linha.valorMensalCalculado;
    brutoHistorico[linha.idNodo] = linha.historico;
    if (linha.valorMensalInteiro != null) inteiroDoErp.add(linha.idNodo);
  });
  const pais = linhas.filter((linha) => relativo(linha) < niveis).sort((a, b) => relativo(a) - relativo(b));
  const alvos = raiz === RAIZ_REVISAO ? [] : [raiz];
  alvos.push(...pais.map((linha) => linha.idNodo));
  alvos.forEach((paiId) => {
    const pai = nos[paiId];
    const ids = porPai[paiId] ?? [];
    if (!pai || ids.length === 0) return;
    const pesos = ids.map((id) => bruto[id] ?? 0);
    const soma = pesos.reduce((acc, peso) => acc + peso, 0);
    const inteirosDoErp = ids.every((id) => inteiroDoErp.has(id));
    if (!inteirosDoErp && soma > 0 && !pesos.some((peso) => peso < 0) && Math.abs(pai.calculado - soma) < 1) {
      distribuirProporcional(pai.calculado, pesos).forEach((parte, i) => {
        const filho = nos[ids[i]!];
        if (filho) filho.calculado = parte;
      });
    }
    reconciliarHistorico(nos, pai, ids, brutoHistorico);
  });
}

/**
 * Mesma reconciliação de `reconciliarFilhos`, mês a mês, para o histórico de
 * vendas: cada nó foi arredondado para cima por conta própria, então a soma
 * dos filhos passava do pai em alguns reais. Aqui o inteiro do pai em cada
 * mês é repartido entre os filhos pelos maiores restos. Um mês em que o pai
 * não é a soma dos filhos (ruído além do arredondamento, venda negativa)
 * fica como veio. A tolerância é 2, e não 1: o inteiro do pai pode já ter
 * sido deslocado em menos de R$ 1 pela reconciliação do nível acima.
 */
function reconciliarHistorico(
  nos: Record<string, NoMeta>,
  pai: NoMeta,
  ids: string[],
  brutoHistorico: Record<string, number[]>,
) {
  pai.historico.forEach((totalPai, mes) => {
    const pesos = ids.map((id) => brutoHistorico[id]?.[mes] ?? 0);
    const soma = pesos.reduce((acc, peso) => acc + peso, 0);
    if (soma <= 0 || pesos.some((peso) => peso < 0) || Math.abs(totalPai - soma) >= 2) return;
    distribuirProporcional(totalPai, pesos).forEach((parte, i) => {
      const filho = nos[ids[i]!];
      if (filho) filho.historico[mes] = parte;
    });
  });
}

/**
 * `revisao_nodos` devolve o `nivel` absoluto de tb_flash_nodo (loja = 1), não
 * o relativo à raiz pedida que `RevisaoNodo.nivelRelativo` promete. Com a
 * loja como raiz os dois coincidem; com um nó mais fundo (um N1, por exemplo)
 * não, e `aplicarCarga` deixaria de marcar os filhos dos níveis abaixo dos
 * filhos diretos. Recalcula a profundidade pela cadeia de pais: filho direto
 * da raiz = 1, e cada nível abaixo soma 1 — certo também quando o ERP pula um
 * nível da hierarquia.
 */
export function relativizarNiveis(raiz: string, linhas: RevisaoNodo[]): RevisaoNodo[] {
  const profundidade = new Map<string, number>();
  const ordenadas = [...linhas].sort((a, b) => a.nivelRelativo - b.nivelRelativo);
  ordenadas.forEach((linha) => {
    const pai = linha.idNodoPai;
    profundidade.set(linha.idNodo, pai === raiz || pai === null ? 1 : (profundidade.get(pai) ?? 0) + 1);
  });
  return linhas.map((linha) => ({ ...linha, nivelRelativo: profundidade.get(linha.idNodo) ?? linha.nivelRelativo }));
}

/**
 * Uma carga de `listRevisaoNodos(raiz, niveis)`: além de incorporar as
 * linhas, marca como completos os filhos da raiz e de todo nó do lote que
 * ainda tinha nível abaixo dele dentro do que foi pedido.
 */
export function aplicarCarga(arvore: Arvore, raiz: string, niveis: number, linhas: RevisaoNodo[]): Arvore {
  const nova = incorporar(arvore, linhas);
  const porPai: Record<string, string[]> = {};
  linhas.forEach((linha) => {
    const pai = linha.idNodoPai ?? RAIZ_REVISAO;
    (porPai[pai] ??= []).push(linha.idNodo);
  });

  // Profundidade de cada linha abaixo da raiz pedida (1 = filho direto),
  // pela árvore já incorporada: o `nivel` do ERP é o absoluto de
  // tb_flash_nodo, que só coincide com o relativo quando a raiz é a companhia.
  const nivelRaiz = raiz === RAIZ_REVISAO ? -1 : (nova.nos[raiz]?.nivel ?? -1);
  const relativo = (linha: RevisaoNodo) => (nova.nos[linha.idNodo]?.nivel ?? 0) - nivelRaiz;

  reconciliarFilhos(nova.nos, raiz, porPai, linhas, niveis, relativo);

  const filhos = { ...nova.filhos };
  let lojas = nova.lojas;
  if (raiz === RAIZ_REVISAO) lojas = ordenarPorNome(porPai[RAIZ_REVISAO] ?? [], nova.nos);
  else filhos[raiz] = ordenarPorNome(porPai[raiz] ?? [], nova.nos);
  linhas.forEach((linha) => {
    if (relativo(linha) < niveis) filhos[linha.idNodo] = ordenarPorNome(porPai[linha.idNodo] ?? [], nova.nos);
  });
  return { nos: nova.nos, filhos, lojas };
}

/** Do topo (loja) até o próprio nó, inclusive. */
export function caminhoDe(arvore: Arvore, id: string): string[] {
  const ids: string[] = [];
  let atual = arvore.nos[id];
  while (atual) {
    ids.unshift(atual.id);
    atual = atual.paiId ? arvore.nos[atual.paiId] : undefined;
  }
  return ids;
}

/**
 * Como cada nó carregado fica com os ajustes dados — o mesmo efeito de
 * APLICAR_AJUSTES (MANUAL.md §6), só que sobre o que está na tela:
 *
 * - nó com ajuste próprio vale o informado;
 * - abaixo de um nó ajustado, tudo escala pelo fator informado ÷ calculado
 *   do ancestral ajustado mais próximo (o mais fundo "vence", como no motor);
 * - acima de um nó ajustado, cada ancestral soma a diferença que o ajuste
 *   fez, até a loja — inclusive ancestral com ajuste próprio: a edição mais
 *   funda vence e o ancestral sobe/desce junto (os irmãos não mudam). A
 *   subárvore do ancestral segue escalando pelo valor informado dele.
 *
 * Um ajuste salvo em nó não carregado (ver `localizarRevisao`) entra aqui do
 * mesmo jeito: o caminho dele até a loja está sempre em `nos`.
 */
export function avaliador(arvore: Arvore, ajustes: Valores) {
  const { nos } = arvore;
  const ancestralAjustado = (id: string): string | null => {
    let atual = nos[id]?.paiId ?? null;
    while (atual) {
      if (ajustes[atual] !== undefined) return atual;
      atual = nos[atual]?.paiId ?? null;
    }
    return null;
  };
  const fator = (id: string) => {
    const ancestral = ancestralAjustado(id);
    if (!ancestral) return 1;
    const calculado = nos[ancestral]?.calculado ?? 0;
    // Base zerada não tem proporção a manter: os filhos ficam como estavam e
    // só o próprio nó muda.
    return calculado ? (ajustes[ancestral] ?? 0) / calculado : 1;
  };
  const base = (id: string) => reaisInteiros((nos[id]?.calculado ?? 0) * fator(id));

  const deltas: Record<string, number> = {};
  Object.entries(ajustes).forEach(([id, informado]) => {
    // Vale para todo ancestral, com ou sem ajuste próprio: a edição mais funda
    // vence e soma a diferença acima dela.
    const delta = informado - base(id);
    let atual = nos[id]?.paiId ?? null;
    while (atual) {
      deltas[atual] = (deltas[atual] ?? 0) + delta;
      atual = nos[atual]?.paiId ?? null;
    }
  });

  // O valor de um nó nunca depende de os filhos estarem carregados — senão a
  // loja mudava de valor ao abrir uma categoria. Nó com ajuste próprio ou com a
  // diferença de uma edição mais funda (`deltas`) vale `proprio`; os demais
  // (só escalado) recebe do pai o que sobra depois dos irmãos fixos, repartido
  // pelos maiores restos. Assim a soma fecha no pai, e quem absorve o
  // arredondamento são os irmãos não editados.
  const fixo = (id: string) => ajustes[id] !== undefined || deltas[id] !== undefined;
  const proprio = (id: string) =>
    (ajustes[id] !== undefined ? ajustes[id]! : base(id)) + (deltas[id] ?? 0);
  const memo = new Map<string, number>();
  const ajustada = (id: string): number => {
    const cache = memo.get(id);
    if (cache !== undefined) return cache;
    let valor = proprio(id);
    const paiId = nos[id]?.paiId;
    const irmaos = paiId ? arvore.filhos[paiId] : undefined;
    // O pai só difere da soma dos `calculado` dos filhos quando foi escalado
    // por um ancestral ajustado ou recebeu `deltas`; fora isso o inteiro do ERP já fecha.
    const paiMudou = paiId ? ancestralAjustado(id) !== null || deltas[paiId] !== undefined : false;
    if (!fixo(id) && paiId && irmaos && irmaos.includes(id) && paiMudou) {
      const calculadoPai = nos[paiId]?.calculado ?? 0;
      const somaTodos = irmaos.reduce((acc, irmao) => acc + (nos[irmao]?.calculado ?? 0), 0);
      // Só vale quando o pai do motor é mesmo a soma dos filhos (fora propagar_pai/exclusões).
      if (somaTodos > 0 && Math.abs(calculadoPai - somaTodos) < 1) {
        const livres = irmaos.filter((irmao) => !fixo(irmao));
        const somaPesos = livres.reduce((acc, irmao) => acc + (nos[irmao]?.calculado ?? 0), 0);
        const resto = ajustada(paiId) - irmaos.filter(fixo).reduce((acc, irmao) => acc + proprio(irmao), 0);
        if (somaPesos > 0 && resto >= 0) {
          const partes = distribuirProporcional(
            resto,
            livres.map((irmao) => nos[irmao]?.calculado ?? 0),
          );
          valor = partes[livres.indexOf(id)] ?? valor;
        }
      }
    }
    memo.set(id, valor);
    return valor;
  };
  return { ajustada, fator };
}

/**
 * Reparte `total` (reais inteiros) na proporção de `pesos`, sem sobra nem
 * falta: cada parte vem do piso e os reais que restam vão para os maiores
 * restos. Sem peso nenhum (soma zero), divide igualmente.
 */
export function distribuirProporcional(total: number, pesos: number[]): number[] {
  if (pesos.length === 0) return [];
  const soma = pesos.reduce((acc, peso) => acc + peso, 0);
  const brutos = pesos.map((peso) => (soma > 0 ? (total * peso) / soma : total / pesos.length));
  const partes = brutos.map(Math.floor);
  const sobra = total - partes.reduce((acc, parte) => acc + parte, 0);
  brutos
    .map((bruto, i) => ({ i, resto: bruto - partes[i]! }))
    .sort((a, b) => b.resto - a.resto || a.i - b.i)
    .slice(0, sobra)
    .forEach(({ i }) => {
      partes[i]! += 1;
    });
  return partes;
}

export function mesmosValores(a: Valores, b: Valores) {
  const chavesA = Object.keys(a);
  return chavesA.length === Object.keys(b).length && chavesA.every((k) => a[k] === b[k]);
}
